// DiagnosticPage — isolated feature.
//
// The whole "diagnostic IA" experience lives in this folder. To remove the
// feature: delete this folder + the `<Route path="/diagnostic" ...>` line in
// App.jsx + the optional Header link.
//
// State machine:
//   loading-question  →  asking question  →  loading-question  →  ...  →  loading-doc → showing-doc
//
// The transcript (assistant questions + user answers) lives in component state
// and is sent in full to the backend on every step. No server-side session.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaSpinner } from 'react-icons/fa';

import { BACKEND_MAIN_URL } from '../config';
import DiagnosticDoc from './DiagnosticDoc';
import DiagnosticLanding from './DiagnosticLanding';
import { getExampleDiagnostic } from './exampleMarkdown';
import './diagnostic.css';
import authHeaders from '../authHeaders';

const PHASE = {
  LANDING: 'landing',
  LOADING_Q: 'loading-q',
  ASKING: 'asking',
  LOADING_DOC: 'loading-doc',
  DONE: 'done',
  ERROR: 'error',
};

export default function DiagnosticPage({ onComplete, onExit, embedded = false, inviteToken = null } = {}) {
  // Optional hooks the host (e.g. DiagIA) can pass:
  //   onComplete({ markdown, transcript, language }) — fires once the final
  //     diagnostic is generated. Used to persist server-side.
  //   onExit() — overrides the back-arrow on the LANDING phase; default goes
  //     to the previous browser entry.
  //   embedded — purely cosmetic flag for hosts that render this inside their
  //     own chrome (currently unused but reserved).
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language || 'fr').split('-')[0];

  const [phase, setPhase] = useState(PHASE.LANDING);
  const [transcript, setTranscript] = useState([]); // [{role, content}]
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [maxQuestions, setMaxQuestions] = useState(5);
  const [answer, setAnswer] = useState('');
  const [diagnosticMd, setDiagnosticMd] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const answerInputRef = useRef(null);

  const fetchNext = useCallback(async (currentTranscript) => {
    try {
      const res = await fetch(`${BACKEND_MAIN_URL}/api/diagnostic/next`, {
        method: 'POST',
        // Signed-in users send their session; invited guests (no account) send
        // their DiagIA invitation token instead — each step is an LLM call.
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ language: lang, messages: currentTranscript, invite_token: inviteToken || undefined }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.type === 'question') {
        setCurrentQuestion(data.question);
        setQuestionNumber(data.question_number || 1);
        setMaxQuestions(data.max_questions || 5);
        setTranscript([...currentTranscript, { role: 'assistant', content: data.question }]);
        setPhase(PHASE.ASKING);
      } else if (data.type === 'diagnostic') {
        const md = data.markdown || '';
        setDiagnosticMd(md);
        setPhase(PHASE.DONE);
        // Notify host (e.g. DiagIA) so it can persist the diagnostic
        // server-side. Fire-and-forget — the user-visible answer is already on
        // screen by this point.
        if (typeof onComplete === 'function' && md) {
          try {
            onComplete({ markdown: md, transcript: currentTranscript, language: lang });
          } catch (e) {
            console.warn('onComplete handler threw:', e);
          }
        }
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      console.error('Diagnostic fetch failed:', err);
      setErrorMsg(err.message || 'Unknown error');
      setPhase(PHASE.ERROR);
    }
  }, [lang, onComplete, inviteToken]);

  // No auto-start: the user lands on the marketing screen first and clicks
  // "Lancer un diagnostic" to enter the intake. This effect kicks in only
  // after we leave the LANDING phase for the first time.

  // Autofocus the answer textarea when a new question lands.
  useEffect(() => {
    if (phase === PHASE.ASKING && answerInputRef.current) {
      answerInputRef.current.focus();
    }
  }, [phase, currentQuestion]);

  const submitAnswer = () => {
    const trimmed = answer.trim();
    if (!trimmed) return;
    const nextTranscript = [...transcript, { role: 'user', content: trimmed }];
    setTranscript(nextTranscript);
    setAnswer('');
    // If the user has already given enough answers, the backend will return
    // a diagnostic — show a different loading state so the wait feels right.
    const userAnswersAfter = nextTranscript.filter((m) => m.role === 'user').length;
    const assistantQsBefore = transcript.filter((m) => m.role === 'assistant').length;
    const expectFinalize = userAnswersAfter >= maxQuestions || assistantQsBefore >= maxQuestions;
    setPhase(expectFinalize ? PHASE.LOADING_DOC : PHASE.LOADING_Q);
    fetchNext(nextTranscript);
  };

  const handleKey = (e) => {
    // Enter alone submits; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submitAnswer();
    }
  };

  const restart = () => {
    setTranscript([]);
    setAnswer('');
    setDiagnosticMd('');
    setErrorMsg('');
    setQuestionNumber(1);
    setPhase(PHASE.LANDING);
  };

  const startIntake = () => {
    setTranscript([]);
    setAnswer('');
    setDiagnosticMd('');
    setErrorMsg('');
    setQuestionNumber(1);
    setPhase(PHASE.LOADING_Q);
    fetchNext([]);
  };

  const showExample = () => {
    setDiagnosticMd(getExampleDiagnostic(i18n.language));
    setPhase(PHASE.DONE);
  };

  // ---------- Render ----------

  if (phase === PHASE.LANDING) {
    return (
      <DiagnosticLanding
        onStart={startIntake}
        onShowExample={showExample}
        onExit={() => (typeof onExit === 'function' ? onExit() : navigate(-1))}
      />
    );
  }

  // From any later phase, the "back arrow" goes to the landing (in-page),
  // not out of the route. The user only leaves /diagnostic from the landing.
  const backToLanding = () => setPhase(PHASE.LANDING);

  if (phase === PHASE.DONE) {
    return (
      <DiagnosticDoc markdown={diagnosticMd} onRestart={restart} onBack={backToLanding} />
    );
  }

  return (
    <div className="diag-page">
      <header className="diag-header">
        <button
          type="button"
          className="diag-back"
          onClick={backToLanding}
          aria-label="Back to landing"
        >
          <FaArrowLeft />
        </button>
        <div className="diag-title">
          <span className="diag-eyebrow">{t('diagia.diag.eyebrow', 'Diagnostic IA')}</span>
          <h1>{t('diagia.diag.title', "Cadrage de votre cas d'usage")}</h1>
        </div>
        <div className="diag-progress">
          <div className="diag-progress-bar">
            <div
              className="diag-progress-fill"
              style={{ width: `${Math.min(100, (questionNumber / maxQuestions) * 100)}%` }}
            />
          </div>
          <span className="diag-progress-label">
            {questionNumber} / {maxQuestions}
          </span>
        </div>
      </header>

      <main className="diag-main">
        {phase === PHASE.LOADING_Q && (
          <div className="diag-loading">
            <FaSpinner className="diag-spin" />
            <p>{t('diagia.diag.loading_q', 'Préparation de la question…')}</p>
          </div>
        )}

        {phase === PHASE.LOADING_DOC && (
          <div className="diag-loading">
            <FaSpinner className="diag-spin" />
            <p>{t('diagia.diag.loading_doc', 'Génération de votre diagnostic en cours…')}</p>
            <p className="diag-loading-sub">
              {t('diagia.diag.loading_doc_sub', 'Cela prend généralement moins de 2 minutes.')}
            </p>
          </div>
        )}

        {phase === PHASE.ERROR && (
          <div className="diag-error">
            <p>{t('diagia.diag.error', 'Une erreur est survenue :')} {errorMsg}</p>
            <button type="button" className="diag-btn" onClick={restart}>
              {t('diagia.diag.restart', 'Recommencer')}
            </button>
          </div>
        )}

        {phase === PHASE.ASKING && (
          <div className="diag-card">
            <div className="diag-question-tag">{t('diagia.diag.question', 'Question')} {questionNumber}</div>
            <p className="diag-question">{currentQuestion}</p>
            <textarea
              ref={answerInputRef}
              className="diag-answer"
              placeholder={t('diagia.diag.answer_placeholder', 'Votre réponse…')}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={handleKey}
              rows={4}
            />
            <div className="diag-actions">
              <span className="diag-hint">
                <kbd>{t('diagia.diag.key_enter', 'Entrée')}</kbd> {t('diagia.diag.hint_submit', 'pour valider')} · <kbd>{t('diagia.diag.key_shift_enter', 'Maj+Entrée')}</kbd> {t('diagia.diag.hint_newline', 'pour un saut de ligne')}
              </span>
              <button
                type="button"
                className="diag-btn diag-btn-primary"
                onClick={submitAnswer}
                disabled={!answer.trim()}
              >
                {t('diagia.diag.next', 'Suivant')} <FaArrowRight />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
